// How figures are written: counts, changes, places moved and shares of the
// peak, in the writer's locale and time zone (see Formats). Nothing here
// touches the DOM.

import type { MaybeStats } from "../../shared/api.ts";
import { type Change, type Way, WAYS, changeText as changeTextIn, dayChange, percentFormats, wayOf } from "./web.ts";
import { plural } from "./markup.ts";

// The parts that write figures and times, in the caller's terms: `locale` for
// numbers (undefined is the reader's own), `date` and `hour` to write a unix
// time as a day and as an hour, and `clock` to write an hour of the day (0–23
// UTC) as a time. The browser writes local times; the Worker, which doesn't
// know its reader, writes UTC and says so.
export interface Formats {
  locale: string | undefined;
  date: (t: number) => string;
  hour: (t: number) => string;
  clock: (hour: number) => string;
}

// The writer's formats, set once by setFormats (local-pages.ts and the Worker call it) before
// any page is written. Each page module imports these as live bindings: the
// browser and the Worker each write in one set of formats for as long as
// they run, so module state is all it needs, and plain functions keep their
// names short in the bundle.
export let number: Intl.NumberFormat;
export let date: (t: number) => string;
export let hour: (t: number) => string;
export let clock: (hour: number) => string;
// The 24h change, by the decimal places it's shown to: tenths below 10%,
// whole percent above, where a fraction is noise. Signed for the list rows;
// plain for prose, which says "up" or "down" in words.
let percent: ReturnType<typeof percentFormats>;
export let percentPlain: ReturnType<typeof percentFormats>;

export function setFormats(formats: Formats): void {
  number = new Intl.NumberFormat(formats.locale);
  percent = percentFormats(formats.locale, "exceptZero");
  percentPlain = percentFormats(formats.locale, "auto");
  ({ date, hour, clock } = formats);
}

// A change (see dayChange) as its arrow and signed percent.
export const changeText = (change: Change): string => changeTextIn(change, percent);

// A change as a row's figure, with what it's over (`label`), spelt out
// because the row has no column headings to carry it.
export function changeFigure(ratio: number | null | undefined, label: string): string {
  const change = dayChange(ratio);
  if (!change) return "";

  return `<small class="change ${change.way}">${changeText(change)}
      <span>${label}</span></small>`;
}

export const changeNote = (game: Pick<MaybeStats, "day_change">): string =>
  changeFigure(game.day_change, "24h");

// Where the count sits against the app's all-time high, in whole percent:
// null at the peak, which callers say outright; below it, held under 100 so a
// rounded 99.6 doesn't read as a record, and 0 means under 1%, not no data.
export function peakShare(now: number, peak: number): number | null {
  if (now >= peak) return null;

  return Math.min(Math.round((now / peak) * 100), 99);
}

// How many places an app moved since the ranking a day earlier, as
// { places, way } with places unsigned; null when it wasn't ranked then (new,
// or back from inactive) or the ranking has less than a day of history.
export function rankMove(game: Pick<MaybeStats, "rank" | "day_ago_rank">): { places: number; way: Way } | null {
  if (game.rank == null || game.day_ago_rank == null) return null;

  const places = game.day_ago_rank - game.rank;
  return { places: Math.abs(places), way: wayOf(places) };
}

// Under the rank: the places moved in 24h, or nothing when it held its place.
// The arrow and hidden text say the direction without relying on colour;
// `spelt` shows the text too, where there's no rank column to explain it.
export function moveNote(game: Pick<MaybeStats, "rank" | "day_ago_rank">, spelt = false): string {
  const move = rankMove(game);
  if (!move || move.way === "flat") return "";

  return `<small class="move ${move.way}">${WAYS[move.way]}${move.places}<span
    class="${spelt ? "" : "visually-hidden"}"> ${plural(move.places, "place")} in
    24h</span></small>`;
}

// With `minor`, it's a row's second figure, which narrow lists leave out.
export function peakNote(now: number | null, peak: number | null, minor = false): string {
  // A game with no count has no peak either (both are its app_stats row's).
  if (now == null || peak == null || peak <= 0) return "";

  const cls = minor ? "peak minor" : "peak";
  const share = peakShare(now, peak);
  if (share == null) return `<small class="${cls}">all-time peak</small>`;

  return `<small class="${cls}">${share === 0 ? "&lt;1" : share}% of peak</small>`;
}
